/**
 * @file site-access-time.ts
 * @description Время для режима «Сайт закрыт» (phase19.16, SB138; SC31):
 * подписи «14:20», «завтра в 01:20», «42 мин» по часовому поясу магазина из
 * ответа сервера (`SiteAccessStateDto.timeZone`) и перевод «дата + время
 * магазина» в момент UTC для поля «Откроем примерно в».
 *
 * Пояс передаётся явно, а не берётся из `shop-time.ts`: состояние паузы
 * несёт свой пояс, и подписи в окне не зависят от того, успел ли каркас
 * поставить пояс вкладки. Неизвестный браузеру пояс — запасной
 * (`DEFAULT_SHOP_TIME_ZONE`), чтобы `Intl` не бросал исключение.
 */

import { DEFAULT_SHOP_TIME_ZONE } from "../shop-time";

/** Части даты в поясе магазина. */
interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** Пояс, который понимает браузер; иначе — запасной. */
export function safeTimeZone(zone: string | null | undefined): string {
  if (!zone) return DEFAULT_SHOP_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("ru-RU", { timeZone: zone });
    return zone;
  } catch {
    return DEFAULT_SHOP_TIME_ZONE;
  }
}

function zonedParts(date: Date, zone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: safeTimeZone(zone),
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute") };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** «гггг-мм-дд» дня в поясе магазина. */
export function zonedDayIso(date: Date, zone: string): string {
  const p = zonedParts(date, zone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** «14:20» в поясе магазина. */
export function zonedClock(date: Date, zone: string): string {
  const p = zonedParts(date, zone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** «12.10» в поясе магазина. */
export function zonedDayMonth(date: Date, zone: string): string {
  const p = zonedParts(date, zone);
  return `${pad(p.day)}.${pad(p.month)}`;
}

/** Сколько суток между днями «гггг-мм-дд» (по календарю, без часов). */
function dayDiff(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

/** День относительно `now`: «сегодня», «завтра» или «21.10» — для подписей и окна. */
export function zonedDayWord(date: Date, now: Date, zone: string): string {
  const diff = dayDiff(zonedDayIso(now, zone), zonedDayIso(date, zone));
  if (diff === 0) return "сегодня";
  if (diff === 1) return "завтра";
  return zonedDayMonth(date, zone);
}

/** «в 15:20» (сегодня), «завтра в 01:20», «21.10 в 15:20». */
export function zonedWhen(date: Date, now: Date, zone: string): string {
  const day = zonedDayWord(date, now, zone);
  return day === "сегодня" ? `в ${zonedClock(date, zone)}` : `${day} в ${zonedClock(date, zone)}`;
}

/** «14:20» сегодня, иначе «12.10 14:20» — когда началась пауза. */
export function zonedSince(date: Date, now: Date, zone: string): string {
  return zonedDayIso(date, zone) === zonedDayIso(now, zone) ? zonedClock(date, zone) : `${zonedDayMonth(date, zone)} ${zonedClock(date, zone)}`;
}

/** Длительность для людей: «меньше минуты», «42 мин», «2 ч 5 мин», «3 дн 4 ч». */
export function formatPauseDuration(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 1) return "меньше минуты";
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} ч ${minutes % 60} мин` : `${hours} ч`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} дн ${hours % 24} ч` : `${days} дн`;
}

/** Смещение пояса в момент `ms`: время магазина минус UTC, мс. */
function zoneOffsetMs(ms: number, zone: string): number {
  const p = zonedParts(new Date(ms), zone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ms / 60_000) * 60_000;
}

/**
 * «гггг-мм-дд» + «чч:мм» по времени магазина → момент. Две итерации
 * поправки — верно и в день перевода часов (у поясов, где он есть).
 * Неверный ввод → `null`.
 */
export function zonedToDate(dayIso: string, clock: string, zone: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dayIso);
  const t = /^(\d{2}):(\d{2})$/u.exec(clock);
  if (!d || !t) return null;
  const local = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  if (!Number.isFinite(local)) return null;
  let utc = local - zoneOffsetMs(local, zone);
  utc = local - zoneOffsetMs(utc, zone);
  return new Date(utc);
}
